import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { Switch } from "@/components/ui/switch";
import { useTahun } from "@/hooks/useTahun";
import api from "@/lib/axios";
import { getListRuangCbt, getListUser } from "@/services/getData";
import { RuangCbt, RuangCbtDetail, User } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";

type InputState = {
  id_ref_ruang_cbt_detail: string;
  id_ref_ruang_cbt: string;
  jumlah_sesi: number;
  tanggal_cbt?: string[];
  is_penuh?: boolean;
  link_wag_kelas?: string;
  link_ruang_virtual_daring?: string;
  nama_pengawas_daring?: string;
  waktu_ujicoba_daring?: string;
  hp_pengawas_daring?: string;
  id_user_pewawancara1?: string;
  id_user_pewawancara2?: string;
};

const postData = async (newData: InputState) => {
  const response = await api.post("/setting/ruang-cbt-detail/store", newData);
  return response.data;
};

const patchData = async (newData: InputState) => {
  const response = await api.patch("/setting/ruang-cbt-detail/update", newData);
  return response.data;
};

const deleteData = async (deletedData: { id_ref_ruang_cbt: string }) => {
  const response = await api({
    url: "/setting/ruang-cbt-detail/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: RuangCbtDetail | null;
  closeModal: () => void;
}) {
  const { selectedTahun } = useTahun();

  const { data: listRuangCbt } = useQuery({
    queryKey: ["listRuangCbt", selectedTahun?.label],
    queryFn: () => getListRuangCbt(selectedTahun?.label),
    select: (data) => {
      return data.map((item: RuangCbt) => {
        return {
          label: item.nama_ruang,
          value: `${item.id_ref_ruang_cbt}`,
          tanggal_cbt: item.tanggal_cbt,
        };
      });
    },
  });

  const [openListRuangCbt, setOpenListRuangCbt] = useState(false);
  const [openListVerifikator, setOpenListVerifikator] = useState(false);
  const [openListVerifikator2, setOpenListVerifikator2] = useState(false);
  const [form, setForm] = useState<InputState>({
    id_ref_ruang_cbt_detail: selectedRow?.id_ref_ruang_cbt_detail || "",
    id_ref_ruang_cbt: selectedRow?.id_ref_ruang_cbt || "",
    jumlah_sesi: selectedRow?.jumlah_sesi || 0,
    is_penuh: selectedRow?.is_penuh ?? false,
    link_wag_kelas: selectedRow?.link_wag_kelas || "",
    link_ruang_virtual_daring: selectedRow?.link_ruang_virtual_daring || "",
    nama_pengawas_daring: selectedRow?.nama_pengawas_daring || "",
    waktu_ujicoba_daring: selectedRow?.waktu_ujicoba_daring || "",
    hp_pengawas_daring: selectedRow?.hp_pengawas_daring || "",
    id_user_pewawancara1: selectedRow?.id_user_pewawancara1 || "",
    id_user_pewawancara2: selectedRow?.id_user_pewawancara2 || "",
  });

  const [selectedTanggalCbt, setSelectedTanggalCbt] = useState<string[]>([]);
  const [searchQueryVerifikator, setSearchQueryVerifikator] = useState("");

  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const { data: dataVerifikator, isPending: isPendingDataVerifikator } =
    useQuery({
      queryKey: ["listVerifikator", searchQueryVerifikator],
      queryFn: () =>
        getListUser(searchQueryVerifikator, 10, "wawancara_talent"),
      select: (data) =>
        data?.data &&
        data.data?.map((user: User) => ({
          label: user.nama_lengkap,
          value: `${user.id}`,
        })),
    });

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listRuangCbtDetail"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const newData = { ...form, tanggal_cbt: selectedTanggalCbt };
    mutate(newData);
  };

  const selectedRuangCbt = listRuangCbt?.find(
    ({ value }: { value: string }) => {
      return value == form.id_ref_ruang_cbt;
    },
  );
  const optionsTanggalCbt =
    selectedRuangCbt &&
    selectedRuangCbt?.tanggal_cbt?.map((value: string) => {
      return { label: value, value };
    });

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Ruang CBT"
          description={`Apakah anda yakin akan menghapus data ruang CBT "${selectedRow?.sesi}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Ruang CBT Detail"
            : "Update Data Ruang CBT Detail"
        }
      />

      {isPost && (
        <>
          {" "}
          <div>
            <Label>Ruang CBT</Label>
            <Combobox
              open={openListRuangCbt}
              setOpen={setOpenListRuangCbt}
              value={form.id_ref_ruang_cbt}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_ref_ruang_cbt: value }));
              }}
              options={listRuangCbt}
              placeholder="Pilih ruang CBT"
            />
          </div>
          <div>
            <Label>Jumlah Sesi</Label>
            <Input
              placeholder="Ketik jumlah sesi..."
              value={form.jumlah_sesi}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  jumlah_sesi: Number(event.target.value),
                }))
              }
            />
          </div>
          {(form.id_ref_ruang_cbt || selectedRuangCbt) && (
            <div>
              <Label>Periode</Label>
              <MultiSelect
                options={optionsTanggalCbt || []}
                onValueChange={(value) => {
                  setSelectedTanggalCbt(value);
                }}
                defaultValue={selectedTanggalCbt}
                placeholder="Pilih Tanggal CBT"
                variant="inverted"
                animation={2}
                maxCount={3}
              />
            </div>
          )}
        </>
      )}

      {isPatch && (
        <>
          <div className="flex gap-3">
            <Switch
              id="is_penuh"
              checked={form.is_penuh}
              onCheckedChange={() =>
                setForm((prev) => ({
                  ...prev,
                  is_penuh: !prev.is_penuh,
                }))
              }
            />
            <Label htmlFor="status">Status Penuh (Belum/Penuh)</Label>
          </div>
          <div>
            <Label>Link Wa Grup Kelas</Label>
            <Input
              placeholder="Paste link wa grup kelas..."
              value={form.link_wag_kelas}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  link_wag_kelas: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Link Ruang Virtual</Label>
            <Input
              placeholder="Paste link ruang virtual..."
              value={form.link_ruang_virtual_daring}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  link_ruang_virtual_daring: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Nama Pengawas</Label>
            <Input
              placeholder="Ketik nama pengawas daring..."
              value={form.nama_pengawas_daring}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  nama_pengawas_daring: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>No Hp Pengawas</Label>
            <Input
              placeholder="Ketik nomer hp pengawas daring..."
              value={form.hp_pengawas_daring}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  hp_pengawas_daring: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Waktu Ujicoba Daring</Label>
            <Input
              type="datetime-local"
              value={form.waktu_ujicoba_daring}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  waktu_ujicoba_daring: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Pewawancara 1</Label>
            <Combobox
              serverSide
              open={openListVerifikator}
              setOpen={setOpenListVerifikator}
              value={form.id_user_pewawancara1 || ""}
              setValue={(newValue) => {
                setForm((prev) => ({
                  ...prev,
                  id_user_pewawancara1: newValue,
                }));
              }}
              options={dataVerifikator}
              placeholder="Pilih Verifikator"
              onChange={(event) =>
                setSearchQueryVerifikator(event.target.value)
              }
              searchQueryValue={searchQueryVerifikator || ""}
              isPending={isPendingDataVerifikator}
            />
          </div>
          <div>
            <Label>Pewawancara 2</Label>
            <Combobox
              serverSide
              open={openListVerifikator2}
              setOpen={setOpenListVerifikator2}
              value={form.id_user_pewawancara2 || ""}
              setValue={(newValue) => {
                setForm((prev) => ({
                  ...prev,
                  id_user_pewawancara2: newValue,
                }));
              }}
              options={dataVerifikator}
              placeholder="Pilih Verifikator"
              onChange={(event) =>
                setSearchQueryVerifikator(event.target.value)
              }
              searchQueryValue={searchQueryVerifikator || ""}
              isPending={isPendingDataVerifikator}
            />
          </div>
        </>
      )}

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  );
}
